import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { profilTypeService } from '../../services/api';

// État initial
const initialState = {
  profilTypes: [],
  currentProfilType: null,
  loading: false,
  error: null,
  total: 0
};

// Actions asynchrones
export const fetchProfilTypes = createAsyncThunk(
  'profilTypes/fetchProfilTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await profilTypeService.getAll();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchProfilTypeById = createAsyncThunk(
  'profilTypes/fetchProfilTypeById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await profilTypeService.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createProfilType = createAsyncThunk(
  'profilTypes/createProfilType',
  async (profilTypeData, { rejectWithValue }) => {
    try {
      const response = await profilTypeService.create(profilTypeData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateProfilType = createAsyncThunk(
  'profilTypes/updateProfilType',
  async ({ id, profilTypeData }, { rejectWithValue }) => {
    try {
      const response = await profilTypeService.update(id, profilTypeData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteProfilType = createAsyncThunk(
  'profilTypes/deleteProfilType',
  async (id, { rejectWithValue }) => {
    try {
      await profilTypeService.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const profilTypeSlice = createSlice({
  name: 'profilType',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentProfilType: (state, action) => {
      state.currentProfilType = action.payload;
    },
    clearCurrentProfilType: (state) => {
      state.currentProfilType = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch all profil types
      .addCase(fetchProfilTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfilTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.profilTypes = action.payload.data || action.payload;
        state.total = action.payload.total || state.profilTypes.length;
        state.error = null;
      })
      .addCase(fetchProfilTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch profil type by ID
      .addCase(fetchProfilTypeById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfilTypeById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProfilType = action.payload;
        state.error = null;
      })
      .addCase(fetchProfilTypeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create profil type
      .addCase(createProfilType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProfilType.fulfilled, (state, action) => {
        state.loading = false;
        state.profilTypes.push(action.payload);
        state.error = null;
      })
      .addCase(createProfilType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update profil type
      .addCase(updateProfilType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfilType.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.profilTypes.findIndex(profilType => profilType.id === action.payload.id);
        if (index !== -1) {
          state.profilTypes[index] = action.payload;
        }
        if (state.currentProfilType && state.currentProfilType.id === action.payload.id) {
          state.currentProfilType = action.payload;
        }
        state.error = null;
      })
      .addCase(updateProfilType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete profil type
      .addCase(deleteProfilType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProfilType.fulfilled, (state, action) => {
        state.loading = false;
        state.profilTypes = state.profilTypes.filter(profilType => profilType.id !== action.payload);
        if (state.currentProfilType && state.currentProfilType.id === action.payload) {
          state.currentProfilType = null;
        }
        state.error = null;
      })
      .addCase(deleteProfilType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, setCurrentProfilType, clearCurrentProfilType } = profilTypeSlice.actions;

export default profilTypeSlice.reducer;
